import { inject, observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Link } from "react-router-dom";
import Select from "react-select";
import { CovertAndFormatCentsToDollarsWithDollarSign } from "src/common/Methods/FileUtil";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";
import { Account, AccountPastDue, Invoice } from "src/model/Financial";
import { AccountPastDueRequest } from "src/requests/FinancialRequests";

import { faExternalLinkSquareAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Business, Customer, ExtractFriendlyName, SimpleCustomer } from "../../model/Customer";
import { CustomerListStore } from "../../stores/CustomerListStore";
import { CustomerLink } from "../common/CustomerLink";
import LoadingWidget from "../common/LoadingWidget";
import { InputPercentageGroup } from "../common/SimpleFormComponents";
import { errorMessage, successMessage } from "../common/Toast";

/**
 * Added this interface to use the id on the table
 */
interface AccountPastDueWithId {
    id: number;
    accountPastDue: AccountPastDue;
}

export interface Props {
    customerListStore?: CustomerListStore;
    customerEndpoints?: CustomerEndpoints;
}

export interface State {
    isLoading: boolean;
    interestRate: number;
    wasValidated: boolean;

    selectedAccount: Array<AccountPastDueWithId>;
    data: Array<AccountPastDueWithId>;
}

@inject("customerEndpoints")
@inject("customerListStore")
@inject("configurationStore")
@observer
export default class GenerateInterestInvoices extends React.Component<Props, State> {
    private form: HTMLFormElement | null;

    constructor(props: Props) {
        super(props);
        this.state = {
            isLoading: false,
            interestRate: 1.5,
            wasValidated: false,
            selectedAccount: [],
            data: []
        };
    }

    handleOnSelect = (row: AccountPastDueWithId, isSelect: boolean) => {
        const newAccountPastDue = {
            id: row.id,
            accountPastDue: row.accountPastDue
        };
        if (isSelect) {
            this.setState(() => ({
                selectedAccount: [...this.state.selectedAccount, newAccountPastDue]
            }));
        } else {
            this.setState(() => ({
                selectedAccount: this.state.selectedAccount.filter(x => x.id !== newAccountPastDue.id)
            }));
        }
    };

    handleOnSelectAll = (isSelect: boolean, rows: AccountPastDueWithId[]) => {
        if (isSelect) {
            this.setState(() => ({
                selectedAccount: rows
            }));
        } else {
            this.setState(() => ({ selectedAccount: [] }));
        }
    };

    getAccountsPastDue = async () => {
        this.setState({ isLoading: true });
        try {
            const data = await this.props.customerEndpoints!.getAccountsPastDue();
            const newAccountPastDue = data
                .sort((a, b) => (ExtractFriendlyName(a.customer, true) > ExtractFriendlyName(b.customer, true) ? 1 : -1))
                .map(a => {
                    return {
                        id: a.customer.id,
                        accountPastDue: a
                    };
                });
            this.setState({ data: newAccountPastDue });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    generateInvoices = async () => {
        try {
            const request: Array<AccountPastDueRequest> = this.state.selectedAccount.map(a => {
                const amountInCents = a.accountPastDue.account.pastDueBalance * (this.state.interestRate / 100);
                const obj = {
                    accountId: a.id,
                    amountInCents: amountInCents,
                    template: "interest",
                    params: {
                        interestRate: this.state.interestRate.toString()
                    }
                };
                return obj;
            });
            await this.props.customerEndpoints!.generateInterestInvoices(request);
            successMessage(`Interest invoices have been generated.`);
        } catch (error) {
            errorMessage(error);
        } finally {
            this.setState({ selectedAccount: [] });
            this.getAccountsPastDue();
        }
    };

    submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.form && this.form.checkValidity()) {
            this.setState({ wasValidated: false });
            this.generateInvoices();
        } else {
            this.setState({ wasValidated: true });
        }
    };

    componentDidUpdate(prevProps: Props, prevState: State) {
        // if (prevState.interestRate !== this.state.interestRate) {
        //     this.getAccountsPastDue();
        // }
    }

    componentDidMount() {
        this.getAccountsPastDue();
    }

    render() {
        const { customerListStore } = this.props;

        if (!customerListStore) {
            return <div>No customers found.</div>;
        }

        const selectRow = {
            mode: "checkbox",
            clickToSelect: false,
            selected: this.state.selectedAccount.map(a => a.id),
            onSelect: this.handleOnSelect,
            onSelectAll: this.handleOnSelectAll
        };

        const columns = [
            {
                dataField: "id",
                text: "Customer Name",
                formatter: (cellContent: any, row: AccountPastDueWithId) => {
                    return (
                        <Link to={`/customer/${row.id}/customer-account`} className="d-flex flex-nowrap" target="_blank">
                            <div className="" style={{ whiteSpace: "nowrap" }}>
                                {ExtractFriendlyName(row.accountPastDue.customer)}
                            </div>
                            <FontAwesomeIcon icon={faExternalLinkSquareAlt} className="ml-2 mt-1" />
                        </Link>
                    );
                }
            },
            {
                dataField: "balance",
                text: "Account Balance",
                formatter: (cellContent: any, row: AccountPastDueWithId) => {
                    return CovertAndFormatCentsToDollarsWithDollarSign(row.accountPastDue.account.balance);
                }
            },
            {
                dataField: "amount",
                text: "Past Due Amount",
                formatter: (cellContent: any, row: AccountPastDueWithId) => {
                    return CovertAndFormatCentsToDollarsWithDollarSign(row.accountPastDue.account.pastDueBalance);
                }
            },
            {
                dataField: "interest",
                text: "Interest",
                formatExtraData: {
                    interestRate: this.state.interestRate
                },
                formatter: (cellContent: any, row: AccountPastDueWithId, index: number, formatExtraData: { interestRate: number }) => {
                    const value = row.accountPastDue.account.pastDueBalance * (formatExtraData.interestRate / 100) || 0;
                    return CovertAndFormatCentsToDollarsWithDollarSign(value);
                }
            }
        ];

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Generate Interest Invoices
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-8">
                            <h1>Generate Interest Invoices</h1>
                        </div>
                    </div>
                    <div className="card shadow-sm mr-4">
                        <div className="card-body">
                            <form
                                id="generateInvoiceInterestdate"
                                ref={form => (this.form = form)}
                                onSubmit={this.submit}
                                className={"needs-validation mt-3" + (this.state.wasValidated ? " was-validated" : "")}
                                noValidate={true}
                            >
                                <div className="form-row mb-2">
                                    <div className="col-12">
                                        <div className="alert alert-info text-center">
                                            <strong>Note:</strong> Only Customers with outstanding invoices are listed here.
                                        </div>
                                    </div>
                                </div>
                                <div className="form-row my-2">
                                    <InputPercentageGroup
                                        label="Montly Interest Rate:"
                                        className="col-12 col-md-3"
                                        type="number"
                                        name="percentage"
                                        isRequired={true}
                                        disabled={false}
                                        isInvalid={this.state.wasValidated}
                                        value={(this.state.interestRate && this.state.interestRate.toString()) || ""}
                                        onChange={e => this.setState({ interestRate: e.target.value })}
                                        step={0.01}
                                    />
                                </div>
                                <div className="form-row mb-4">
                                    <div className="col-12">
                                        <button
                                            type="submit"
                                            className={`btn ${this.state.selectedAccount.length > 0 ? "btn-primary" : "btn-secondary"}`}
                                            disabled={this.state.selectedAccount.length === 0}
                                        >
                                            Generate Interest Invoices
                                            <span className="badge badge-secondary ml-2">{this.state.selectedAccount.length}</span>
                                        </button>
                                    </div>
                                </div>
                            </form>
                            <div className="form-row">
                                <div className="col-12">
                                    <div className="table-responsive">
                                        {
                                            <BootstrapTable
                                                keyField="id"
                                                remote={true}
                                                hover={true}
                                                bordered={false}
                                                columns={columns}
                                                bootstrap4={true}
                                                data={this.state.data}
                                                selectRow={selectRow}
                                                noDataIndication={() => (
                                                    <div>{this.state.isLoading ? <LoadingWidget /> : <span>No results found.</span>}</div>
                                                )}
                                            />
                                        }
                                        {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                        Otherwise the `noDataIndication` wouldn't work */}
                                        {customerListStore.loading && <div />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/customer/GenerateInterestInvoices.tsx